import {
    Button,
    Card,
    CardContent,
    Typography,
} from "@material-ui/core";
import { withStyles } from "@material-ui/core/styles";
import {
    Autocomplete,
    Box,
    CircularProgress,
    IconButton,
    LinearProgress,
    Step,
    StepConnector,
    StepLabel,
    Stepper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField
} from "@mui/material";
import Papa from "papaparse";
import { ArrowClockwise, File, FileArrowUp, Gear, TrashSimple, X } from "phosphor-react";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { fget } from "../../../API/callsAPI";
import { MovieColabContext } from "../../../Context/MovieColabContext";
import { getTrackableIconFromAssetName } from "../Trackables/trackablesData";
import { downloadExampleCsv } from "./downloadExampleCsv";
import useStyles from "./CSVImportModal.styles";
const STEPS = ["Choose Document", "Define Mappings", "Summary"];

const resolveAssetType = (value, allowedTypes) => {
    const trimmed = String(value ?? "").trim();
    if (!trimmed) return null;
    return allowedTypes.find((t) => t.toLowerCase() === trimmed.toLowerCase()) ?? null;
};

const autocompleteSlotProps = {
    paper: {
        sx: {
            maxHeight: "300px",
            backgroundColor: "#252A38",
            color: "#F9F9F9",
            "& .MuiAutocomplete-option": { fontSize: "12px" },
            "& .MuiAutocomplete-option.Mui-focused": { backgroundColor: "rgba(108, 101, 217, 0.25)" },
        },
    },
    listbox: { sx: { maxHeight: "300px", overflow: "auto", padding: 0 } },
};

const autocompleteSx = {
    minWidth: 160,
    marginLeft: "10px",
    "& .MuiOutlinedInput-root": {
        borderRadius: "5px",
        color: "#F9F9F9",
        fontSize: { xs: "10px", sm: "10px", md: "10px", lg: "12px", xl: "14px" },
        padding: "2px 4px",
        "& fieldset": { borderColor: "rgba(249, 249, 249, 0.15)" },
        "&:hover fieldset": { borderColor: "rgba(249, 249, 249, 0.25)" },
        "&.Mui-focused fieldset": { borderColor: "rgba(108, 101, 217, 0.6)" },
    },
    "& .MuiAutocomplete-popupIndicator, & .MuiAutocomplete-clearIndicator": { color: "#606479" },
};

const CustomConnector = withStyles((theme) => ({
    root: {
        flex: 1,
        padding: "0 8px",
    },
    line: {
        borderColor: theme.palette.secondary.light,
        borderTopWidth: 2,
    },
}))(StepConnector);

export default function CSVImportModal({
    createInBulk,
    trackableType,
    closeModal,
    projectId,
    requiredFields,
}) {
    const params = useParams();
    const { trackableClass } = useContext(MovieColabContext);
    const classes = useStyles();
    const [activeStep, setActiveStep] = useState(0);
    const [selectedFile, setSelectedFile] = useState(null);
    const [isDraggingOver, setIsDraggingOver] = useState(false);
    const [tableHeaders, setTableHeaders] = useState([]);
    const [tableData, setTableData] = useState([]);
    const [columnMappings, setColumnMappings] = useState([]);
    const [isImporting, setIsImporting] = useState(false);
    const [defaultFields, setDefaultFields] = useState([]);
    const [trackableStatuses, setTrackableStatuses] = useState([]);
    const [assetTypeChoices, setAssetTypeChoices] = useState([]);
    const [hasInvalidValues, setHasInvalidValues] = useState(false);


    const getDefaultFields = async () => {
        const trackableId = trackableClass.find(trackable => trackable.model === trackableType)?.id;
        try {
            const res = await fget({
                url: `/trackables/trackable-field/?project=${projectId}&trackable=${trackableId}`,
            });
            const defaultFields = res.data.default_fields;
            const customFields = res.data.custom_fields;
            setDefaultFields([...defaultFields, ...customFields]);
        } catch (error) {
            console.error("Error getting default fields", error);
        }
    };

    const getTrackableStatuses = async () => {
        const trackableId = trackableClass.find(trackable => trackable.model === trackableType)?.id;
        if (!trackableId) return;
        try {
            const res = await fget({
                url: `trackables/status-list/${trackableId}/?project=${projectId}`,
            });
            setTrackableStatuses(res?.data?.results || []);
        } catch (error) {
            console.error("Error getting statuses", error);
            setTrackableStatuses([]);
        }
    };

    const getAssetTypeChoices = async () => {
        try {
            const res = await fget({ url: `trackables/asset-type-choices/` });
            setAssetTypeChoices(res?.data?.asset_type_choices ?? []);
        } catch (error) {
            console.error("Error getting asset types", error);
            setAssetTypeChoices([]);
        }
    };

    useEffect(() => {
        if (trackableType && projectId && trackableClass.length > 0) {
            getDefaultFields();
            getTrackableStatuses();
            if (trackableType === "asset") {
                getAssetTypeChoices();
            } else {
                setAssetTypeChoices([]);
            }
        }
    }, [trackableType, projectId, trackableClass]);

    const handleFileInput = (e) => {
        const file = e.target.files[0];
        if (file && file.name.endsWith(".csv")) {
            setSelectedFile(file);
        } else {
            toast.error("Please select a valid CSV file");
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        setIsDraggingOver(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        setIsDraggingOver(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDraggingOver(false);
        handleFileInput(e.dataTransfer.files[0]);
    };

    const handleRemoveFile = (e) => {
        e.stopPropagation();
        setSelectedFile(null);
    };

    useEffect(() => {
        if (selectedFile) {
            Papa.parse(selectedFile, {
                header: true,
                skipEmptyLines: true,
                complete: (results) => {
                    setTableHeaders(results.meta.fields);
                    setTableData(results.data);
                }
            });
        }
    }, [selectedFile]);

    const handleImport = async () => {
        setIsImporting(true);
        try {
            const statusCsvHeader = Object.keys(columnMappings).find(
                (csvHeader) => columnMappings[csvHeader] === "status"
            );
            const typeCsvHeader =
                trackableType === "asset"
                    ? Object.keys(columnMappings).find((h) => columnMappings[h] === "type")
                    : null;

            const statusCodeToId = (trackableStatuses || []).reduce((acc, status) => {
                if (status?.code) {
                    acc[String(status.code).trim().toUpperCase()] = status.id;
                }
                return acc;
            }, {});

            const transformedTableData = tableData.map((row, rowIndex) => {
                const transformedRow = { ...row };
                if (statusCsvHeader) {
                    const submittedStatusCode = String(row[statusCsvHeader] ?? "").trim().toUpperCase();
                    if (!submittedStatusCode) {
                        throw new Error(`Row ${rowIndex + 1}: status code is required.`);
                    }
                    const statusId = statusCodeToId[submittedStatusCode];
                    if (!statusId) {
                        const allowedCodes = Object.keys(statusCodeToId).join(", ");
                        throw new Error(
                            `Row ${rowIndex + 1}: invalid status code "${row[statusCsvHeader]}". Allowed codes: ${allowedCodes || "none configured"}.`
                        );
                    }
                    transformedRow[statusCsvHeader] = statusId;
                }
                if (typeCsvHeader) {
                    const resolvedType = resolveAssetType(row[typeCsvHeader], assetTypeChoices);
                    if (!resolvedType) {
                        throw new Error(
                            `Row ${rowIndex + 1}: invalid type "${row[typeCsvHeader]}". Allowed: ${assetTypeChoices.join(", ")}.`
                        );
                    }
                    transformedRow[typeCsvHeader] = resolvedType;
                }
                return transformedRow;
            });

            const res = await createInBulk(transformedTableData, columnMappings, trackableType, defaultFields);
            if (res) {
                toast.success("Imported successfully");
                closeModal();
            } else {
                toast.error("Error importing");
            }
        } catch (err) {
            toast.error(
                err?.message ?? err?.response?.data?.detail ?? "Error importing"
            );
        } finally {
            setIsImporting(false);
        }
    };

    return (
        <div className={classes.root}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
        >
            <Card className={classes.card}>
                <CardContent className={classes.cardContent}>
                    {/* Header */}
                    <div className={classes.header}>
                        <span className={classes.title}>Import from a spreadsheet</span>
                        <Button className={classes.closeButton} onClick={closeModal}>
                            <X size={32} weight="bold" />
                        </Button>
                    </div>
                    {activeStep === 1 && <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "15px", marginLeft: "-8px" }}>

                        <Typography className={classes.lightText}>To create new fields, go to the settings.<IconButton className={classes.closeButton} onClick={() => {
                            const trackableId = trackableClass?.find((t) => t.model === trackableType.toLowerCase())?.id ?? 12;
                            window.open(`${window.location.origin}/${params.projectId}/settings/field-templates?selectedTrackable=${trackableId}`, "_blank");
                        }}><Gear size={30} color="#6C65D9" /> </IconButton></Typography>
                        <Typography className={classes.lightText}>To refresh the fields, click the refresh icon.

                            <IconButton className={classes.closeButton} onClick={() => {
                                getDefaultFields();
                                getTrackableStatuses();
                                if (trackableType === "asset") getAssetTypeChoices();
                            }}><ArrowClockwise size={30} color="#6C65D9" /> </IconButton>
                        </Typography>

                    </Box>}

                    {/* Stepper */}
                    <Stepper
                        activeStep={activeStep}
                        connector={<CustomConnector />}
                        className={classes.stepper}

                    >
                        {STEPS.map((label, index) => (
                            <Step key={label}>
                                <StepLabel>
                                    {label}
                                </StepLabel>
                            </Step>
                        ))}
                    </Stepper>

                    <Box sx={{ display: "flex", justifyContent: "flex-start", mb: 1.5 }}>
                        <Button
                            size="small"
                            variant="outlined"
                            onClick={() => downloadExampleCsv(trackableType)}
                            style={{
                                color: "#6C65D9",
                                textTransform: "none",
                            }}
                        >
                            Download example CSV
                        </Button>
                    </Box>

                    {/* STEP COMPONENTS */}
                    {activeStep === 0 && <ChooseDocumentStep
                        selectedFile={selectedFile}
                        handleRemoveFile={handleRemoveFile}
                        handleFileInput={handleFileInput}
                        handleDragOver={handleDragOver}
                        handleDragLeave={handleDragLeave}
                        handleDrop={handleDrop}
                        isDraggingOver={isDraggingOver}
                        trackableType={trackableType}
                    />}
                    {activeStep === 1 &&
                        <DefineMappingsStep
                            tableHeaders={tableHeaders}
                            tableData={tableData}
                            columnMappings={columnMappings}
                            setColumnMappings={setColumnMappings}
                            defaultFields={defaultFields}
                            trackableStatuses={trackableStatuses}
                            trackableType={trackableType}
                            assetTypeChoices={assetTypeChoices}
                            setHasInvalidValues={setHasInvalidValues}
                            requiredFields={requiredFields}
                        />}
                    {activeStep === 2 &&
                        <SummaryStep
                            columnMappings={columnMappings}
                            tableData={tableData}
                            selectedFile={selectedFile}
                            trackableType={trackableType}
                        />}

                    {/* Footer */}
                    <div className={classes.footer}>
                        {activeStep > 0 ? <Button
                            className={`${classes.backButton} ${!selectedFile ? classes.backButtonDisabled : ""
                                }`}
                            disabled={activeStep === 0}
                            disableElevation
                            onClick={() => {
                                setActiveStep(activeStep - 1);
                                toast.success("Back button clicked");
                            }}
                        >
                            Back
                        </Button> : <Box />}
                        {activeStep === 2 ?
                            <Button
                                className={classes.nextButton}
                                disableElevation
                                disabled={isImporting}
                                onClick={handleImport}
                                startIcon={isImporting ? <CircularProgress size={16} color="inherit" /> : null}
                            >
                                {isImporting ? "Importing..." : "Import into Project"}
                            </Button> :
                            <Button
                                className={`${classes.nextButton} ${!selectedFile ? classes.nextButtonDisabled : ""
                                    }`}
                                disabled={!selectedFile || hasInvalidValues}
                                disableElevation
                                onClick={() => setActiveStep(activeStep + 1)}
                            >
                                Next
                            </Button>}
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}

const ChooseDocumentStep = ({ selectedFile, handleRemoveFile, handleFileInput, isDraggingOver }) => {
    const classes = useStyles();
    const fileInputRef = useRef(null);

    return (
        <>
            {selectedFile ? (
                <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between" }}>
                    <div>
                        <Typography className={classes.fileName}>{selectedFile.name}</Typography>
                        <Typography className={classes.fileSize}>{selectedFile.size} KB . Completed</Typography>
                        <LinearProgress variant="determinate" sx={{ width: "600px", height: "4px", color: "#6C65D9", backgroundColor: "#252A38" }} />
                    </div>
                    <IconButton
                        className={classes.removeFileBtn}
                        onClick={handleRemoveFile}
                    >
                        <TrashSimple size={20} color="#D61F1F" />
                    </IconButton>
                </div>
            ) : (<div
                className={`${classes.uploadArea} ${isDraggingOver ? classes.uploadAreaDragging : ""
                    }`}
                onClick={() => !selectedFile && fileInputRef.current?.click()}
            >
                <div className={classes.uploadPlaceholder}>
                    <FileArrowUp size={30} className={classes.uploadIcon} />
                    <span className={classes.uploadText}>
                        Choose or drop <strong>.csv</strong>
                    </span>
                </div>
            </div>)}

            <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                style={{ display: "none" }}
                onChange={handleFileInput}
            />
        </>
    );
}

const DefineMappingsStep = ({ tableHeaders, tableData, columnMappings, setColumnMappings, defaultFields, trackableStatuses, trackableType, assetTypeChoices, setHasInvalidValues, requiredFields }) => {
    const classes = useStyles();

    const fieldOptions = useMemo(
        () => [
            { name: "none", label: "None" },
            ...defaultFields.map((f) => ({ name: f.name, label: f.name })),
        ],
        [defaultFields]
    );

    const {
        allowedStatusCodes,
        invalidStatusRows,
        hasInvalidStatusCodes,
        invalidTypeRows,
        hasInvalidTypes,
    } = useMemo(() => {
        const statusCsvHeader = Object.keys(columnMappings).find(
            (csvHeader) => columnMappings[csvHeader] === "status"
        );
        const typeCsvHeader =
            trackableType === "asset"
                ? Object.keys(columnMappings).find((h) => columnMappings[h] === "type")
                : null;

        const allowedStatusCodes = (trackableStatuses || [])
            .map((status) => String(status?.code || "").trim().toUpperCase())
            .filter(Boolean);
        const allowedStatusCodeSet = new Set(allowedStatusCodes);

        const invalidStatusRows = statusCsvHeader
            ? tableData
                .map((row, rowIndex) => {
                    const rawValue = row[statusCsvHeader];
                    const submittedCode = String(rawValue ?? "").trim().toUpperCase();
                    const isValid = submittedCode && allowedStatusCodeSet.has(submittedCode);
                    return isValid ? null : { rowNumber: rowIndex + 1, value: rawValue };
                })
                .filter(Boolean)
            : [];

        const invalidTypeRows = typeCsvHeader
            ? tableData
                .map((row, rowIndex) => {
                    const rawValue = row[typeCsvHeader];
                    const isValid = resolveAssetType(rawValue, assetTypeChoices);
                    return isValid ? null : { rowNumber: rowIndex + 1, value: rawValue };
                })
                .filter(Boolean)
            : [];

        if (invalidStatusRows.length > 0 || invalidTypeRows.length > 0) {
            setHasInvalidValues(true);
        }
        else {
            setHasInvalidValues(false);
        }

        return {
            allowedStatusCodes,
            invalidStatusRows,
            hasInvalidStatusCodes: Boolean(statusCsvHeader && invalidStatusRows.length > 0),
            invalidTypeRows,
            hasInvalidTypes: Boolean(typeCsvHeader && invalidTypeRows.length > 0),
        };
    }, [columnMappings, tableData, trackableStatuses, trackableType, assetTypeChoices]);

    return (
        <div style={{ width: "90vw", height: "70vh" }}>

            <Typography className={classes.lightText} style={{ marginBottom: "5px", fontSize: "14px" }}>
                {requiredFields.join(", ")} is required.
            </Typography>

            {hasInvalidStatusCodes && (
                <Box
                    sx={{
                        background: "rgba(214, 31, 31, 0.12)",
                        border: "1px solid rgba(214, 31, 31, 0.4)",
                        borderRadius: "6px",
                        padding: "10px 12px",
                        marginBottom: "12px",
                    }}
                >
                    <Typography className={classes.lightText}>
                        Status is mapped, but {invalidStatusRows.length} row(s) have values that do not match allowed status codes.
                        Please upload status <strong>code</strong> values (example: {allowedStatusCodes.slice(0, 5).join(", ")}).
                    </Typography>
                </Box>
            )}
            {hasInvalidTypes && (
                <Box
                    sx={{
                        background: "rgba(214, 31, 31, 0.12)",
                        border: "1px solid rgba(214, 31, 31, 0.4)",
                        borderRadius: "6px",
                        padding: "10px 12px",
                        marginBottom: "12px",
                    }}
                >
                    <Typography className={classes.lightText}>
                        Type is mapped, but {invalidTypeRows.length} row(s) have values that are not allowed asset types.
                        Allowed: {assetTypeChoices.join(", ")}.
                    </Typography>
                </Box>
            )}

            <TableContainer sx={{ maxHeight: "68vh", overflowY: "auto" }}>
                <Table stickyHeader>
                    <TableHead>
                        <TableRow>
                            {tableHeaders.map((header, key) => (
                                <TableCell key={key} className={classes.headerCell}>
                                    {header} <span className={classes.lightText}>from</span>
                                    <Autocomplete
                                        size="small"
                                        disableClearable
                                        options={fieldOptions}
                                        getOptionLabel={(option) => option.label}
                                        isOptionEqualToValue={(a, b) => a.name === b.name}
                                        value={
                                            fieldOptions.find(
                                                (o) => o.name === (columnMappings[header] ?? "none")
                                            ) ?? fieldOptions[0]
                                        }
                                        onChange={(_, newValue) =>
                                            setColumnMappings({
                                                ...columnMappings,
                                                [header]: newValue?.name ?? "none",
                                            })
                                        }
                                        slotProps={autocompleteSlotProps}
                                        sx={autocompleteSx}
                                        renderInput={(params) => (
                                            <TextField {...params} variant="outlined" placeholder="Field" />
                                        )}
                                    />
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {tableData.map((row, rowIndex) => (
                            <TableRow key={rowIndex}>
                                {tableHeaders.map((header, colIndex) => (
                                    <TableCell key={colIndex} className={classes.tableCell}>
                                        {row[header] ?? "—"}
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </div>
    );
}

const SummaryStep = ({ tableData, selectedFile, trackableType }) => {
    const classes = useStyles();

    const trackableIcon = getTrackableIconFromAssetName(trackableType);

    const rows = [
        {
            name: "Document",
            Icon: <File size={18} />,
            items: [
                { label: selectedFile.name, value: "" },
            ],
        },
        {
            name: trackableType,
            Icon: trackableIcon,
            items: [
                { label: "Rows Created", value: tableData.length },
            ],
        }
    ];

    return (
        <div className={classes.summaryContainer}>
            {rows.map((row, idx) => (
                <div key={idx} className={classes.summaryColumn}>
                    <div className={classes.summaryColumnHeader}>
                        {row.Icon}
                        <Typography className={classes.summaryColumnTitle}>{row.name}</Typography>
                    </div>
                    {row.items.map((item, itemIdx) => (
                        <div key={itemIdx} className={classes.summaryRow}>
                            <Typography className={classes.summaryLabel}>{item.label}</Typography>
                            <Typography className={classes.summaryValue}>{item.value}</Typography>
                        </div>
                    ))}
                </div>
            ))}
        </div>
    );
}
